/**
 * DelRicht Brand System — shared design tokens and styled components.
 * Matches PMS application design language.
 * Import: import { B, F, Panel, PanelHeader, MetricCard, StatRow, BarRow } from '@/theme/brand';
 */
import React from 'react';

// --- Color Palette ---

/**
 * ONE HUE, ONE JOB. Teal used to mean five things at once on /work — the Dial button,
 * the queue counts, the Web-chat channel chip, the `eligible` verdict and all eleven
 * criterion checkmarks. Eighteen teal objects, four unrelated meanings, so the hue
 * stopped carrying information. The palette did not grow to fix that; the roles were
 * reassigned:
 *
 *   navy   -> action, navigation, counts, anything clickable
 *   teal   -> CLINICAL STATUS only (criterion met, eligible)
 *   gold   -> needs a human's attention
 *   red    -> failed / blocked
 *
 * INK VARIANTS ARE NOT OPTIONAL. Measured against white: `green` is 3.47:1 and `gold` is
 * 2.10:1 — both fail WCAG AA for text (4.5:1), though `green` clears the 3:1 bar for
 * glyphs and borders. Use `greenInk` / `goldDark` whenever the colour renders as WORDS.
 * `greenInk` was added 2026-09-04; `goldDark`/`goldInk` already existed for exactly this
 * reason and the teal never got the same treatment.
 */
export const B = {
  blue: '#00265E',
  blueMid: '#003d8f',
  blueLight: '#F0F4FF',
  green: '#00999D',       // 3.47:1 on white — glyphs and fills only, never body text
  greenInk: '#00676A',    // 6.67:1 on white, 5.95:1 on greenLight — the text-safe teal
  greenLight: '#E6F5F5',
  gold: '#D4AF37',        // 2.10:1 on white — fills, borders and rails only
  goldDark: '#806611',    // was #9a7d16 (3.95:1) — still short of AA; now 5.49:1
  goldInk: '#3a2f05',
  goldLight: '#FBF3D8',   // caution panel background
  purple: '#6f42c1',      // SMS channel / appointment-booked accent
  red: '#C43B4C',
  redLight: '#FCEDEF',
  orange: '#E67E22',
  // Three surface values, and organisation comes from stepping between them rather than
  // from adding hues: page -> recessed block -> card.
  bg: '#F4F5F8',
  card: '#FFFFFF',
  border: '#DDE1E8',
  borderFine: '#EBEEF2',
  text: '#0A1A2E',
  textSec: '#4D5E70',
  textMute: '#6B7787',    // 4.55:1 — was #8A95A3 (3.04:1), which failed AA as a label
};

/**
 * LEAD-SOURCE PALETTE: a deliberate, scoped exception to "one hue, one job".
 *
 * Decision 2026-10-05 (Marshall, /sources card view): "color per source". On /sources
 * only, each lead source keeps one colour everywhere (tile accent, card bar, chart
 * series) so a glance answers "which channel". Use it ONLY for source identity on
 * /sources; nowhere else, and never for status (red / gold / teal keep their jobs).
 * Fills and borders only: put text in B.text, not in these colours (the orange and
 * violet do not meet text contrast on white).
 */
export const SOURCE = {
  meta: '#1877F2',        // Facebook blue: recognisable at a glance as "Meta"
  chatbot: '#7C4DDB',     // violet: the conversation channel
  web_forms: '#E67E22',   // orange: the website forms (= B.orange)
  other: '#8A95A3',
} as const;
export const SOURCE_TINT = {
  meta: 'rgba(24,119,242,0.10)',
  chatbot: 'rgba(124,77,219,0.10)',
  web_forms: 'rgba(230,126,34,0.12)',
  other: 'rgba(138,149,163,0.12)',
} as const;

// --- Typography ---

export const F = {
  head: "'Proxima Nova', 'Montserrat', system-ui, sans-serif",
  body: "'Poppins', system-ui, sans-serif",
  data: "'Open Sans', system-ui, sans-serif",
};

// --- Semantic color helpers ---

export const statusColor = (status: string): string => {
  const map: Record<string, string> = {
    success: B.green, active: B.green, completed: B.green, eligible: B.green,
    // goldDark, NOT gold: #D4AF37 is 2.10:1 on white and this helper's output is used as
    // TEXT at every call site, so a `warning` status was rendering effectively invisible.
    warning: B.goldDark, pending: B.goldDark, uncertain: B.goldDark,
    danger: B.red, error: B.red, ineligible: B.red, critical: B.red,
    info: B.blueMid, primary: B.blue,
  };
  return map[status?.toLowerCase()] || B.textMute;
};

// --- Spacing scale (px) — use instead of inline magic numbers ---

export const S = { xs: 4, sm: 8, md: 12, lg: 16, xl: 24, xxl: 32 } as const;

// --- Border radius scale (px) ---

export const R = { sm: 6, md: 8, lg: 12, pill: 999 } as const;

// --- Type scale (px) — pair with F.head / F.body / F.data families ---

/**
 * The bottom of this scale used to be `base: 13, sm: 12, xs: 11, tiny: 10` — four steps
 * inside three pixels. One-pixel increments are not perceptible as hierarchy; they only
 * read as inconsistency, so picking between them was a coin flip. Meanwhile there was a
 * 3px cliff from `h3: 16` to `base: 13` with nothing in it, which is exactly the step a
 * section subheading needs.
 *
 * Now ~1.15 between every step, `h4` fills the cliff, and `tiny` is an ALIAS of `xs`
 * rather than a fourth small size — the distinction it claimed to make did not survive
 * being rendered. It is kept as a key so existing call sites do not break; prefer `xs`
 * in new code.
 */
export const T = {
  h1: 24, h2: 20, h3: 17, h4: 15,
  base: 13, sm: 11.5, xs: 10.5,
  /** @deprecated Alias of `xs` — there were never two distinguishable sizes here. */
  tiny: 10.5,
} as const;

// --- Card shadow ---

export const cardShadow = '0 1px 3px rgba(0,38,94,0.04)';
export const cardHoverShadow = '0 4px 8px rgba(0,38,94,0.08)';

// --- Layout constants (shared by Sidebar.tsx and App.tsx so widths can't drift) ---

export const SIDEBAR_WIDTH = 250;
export const SIDEBAR_WIDTH_COLLAPSED = 64;

// --- Styled Components ---

export function Panel({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return React.createElement('div', {
    style: {
      background: B.card,
      borderRadius: 12,
      border: `1px solid ${B.border}`,
      boxShadow: cardShadow,
      overflow: 'hidden',
      ...style,
    },
  }, children);
}

export function PanelHeader({ icon, title, badge, action }: { icon?: string; title: string; badge?: string; action?: React.ReactNode }) {
  return React.createElement('div', {
    style: {
      padding: '14px 18px',
      background: B.blue,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
  },
    React.createElement('div', {
      style: { fontSize: 12, fontWeight: 700, fontFamily: F.head, color: '#fff', letterSpacing: 0.5 },
    },
      icon ? React.createElement('i', { className: `bi ${icon}`, style: { marginRight: 8 } }) : null,
      title,
    ),
    React.createElement('div', { style: { display: 'flex', gap: 8, alignItems: 'center' } },
      badge ? React.createElement('span', {
        style: { fontSize: 9, fontWeight: 700, fontFamily: F.head, color: B.green, background: 'rgba(0,153,157,0.15)', padding: '2px 8px', borderRadius: 4, letterSpacing: 0.8 },
      }, badge) : null,
      action || null,
    ),
  );
}

export function PanelHeaderLight({ icon, title, badge }: { icon?: string; title: string; badge?: string }) {
  return React.createElement('div', {
    style: {
      padding: '12px 18px',
      borderBottom: `1px solid ${B.borderFine}`,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
  },
    React.createElement('div', {
      style: { fontSize: 13, fontWeight: 600, fontFamily: F.body, color: B.text },
    },
      icon ? React.createElement('i', { className: `bi ${icon}`, style: { marginRight: 8, color: B.textMute } }) : null,
      title,
    ),
    badge ? React.createElement('span', {
      style: { fontSize: 10, fontWeight: 600, fontFamily: F.data, color: B.textMute },
    }, badge) : null,
  );
}

export function MetricCard({ label, value, sub, color }: { label: string; value: string | number; sub?: string; color?: string }) {
  return React.createElement('div', {
    style: {
      background: B.card,
      borderRadius: 12,
      border: `1px solid ${B.border}`,
      boxShadow: cardShadow,
      padding: '16px 18px',
      borderLeft: `4px solid ${color || B.blue}`,
    },
  },
    React.createElement('div', {
      style: { fontSize: 10, fontWeight: 600, fontFamily: F.head, color: B.textMute, textTransform: 'uppercase' as const, letterSpacing: 0.8, marginBottom: 6 },
    }, label),
    React.createElement('div', {
      style: { fontSize: 26, fontWeight: 700, fontFamily: F.data, color: B.text, lineHeight: 1.1 },
    }, value),
    sub ? React.createElement('div', {
      style: { fontSize: 11, fontFamily: F.body, color: B.textSec, marginTop: 4 },
    }, sub) : null,
  );
}

export function StatRow({ icon, label, value, valueColor }: { icon?: string; label: string; value: string | number; valueColor?: string }) {
  return React.createElement('div', {
    style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: `1px solid ${B.borderFine}` },
  },
    React.createElement('span', {
      style: { fontSize: 12, fontFamily: F.body, color: B.textSec },
    },
      icon ? React.createElement('i', { className: `bi ${icon}`, style: { marginRight: 8, color: B.textMute } }) : null,
      label,
    ),
    React.createElement('span', {
      style: { fontSize: 13, fontWeight: 700, fontFamily: F.data, color: valueColor || B.green },
    }, value),
  );
}

export function BarRow({ label, value, max, color }: { label: string; value: number; max: number; color?: string }) {
  const w = max > 0 ? Math.max((value / max) * 100, 3) : 0;
  return React.createElement('div', {
    style: { display: 'flex', alignItems: 'center', gap: 10, padding: '5px 0' },
  },
    React.createElement('span', {
      style: { fontSize: 11, fontFamily: F.body, color: B.textSec, width: 120, flexShrink: 0, textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' as const },
      title: label,
    }, label),
    React.createElement('div', {
      style: { flex: 1, height: 8, background: B.borderFine, borderRadius: 4, overflow: 'hidden' },
    },
      React.createElement('div', {
        style: { height: '100%', width: `${w}%`, background: color || B.blue, borderRadius: 4, transition: 'width 0.4s ease' },
      }),
    ),
    React.createElement('span', {
      style: { fontSize: 11, fontWeight: 700, fontFamily: F.data, color: B.text, width: 32, textAlign: 'right' as const },
    }, value),
  );
}
